(() => {
  const CONFIG = {
    debug: false,
    channelHostSelector: '[id^="videowise_channel_"] > div',
    channelCardSelector: '.vw_channel_video_card_wrapper',
    channelCardTitleSelector: '.card_title',
    channelPlayerSelector: '.vw_channel_video_player',
    channelPlayerVideoSelector: '.vw_channel_video_player video.video_tag',
    rsvpReminderSelector: '#vw--rsvp-ls--reminder, #vw--rsvp--reminder',
    rsvpSuccessSelector: '#vw--rsvp-ls--success, #vw--rsvp--success',
    rsvpCloseButtonSelector: '.vw--rsvp-ls--reminder--close--button, #vw--rsvp--reminder--top button, #vw--rsvp--success .vw--rsvp-ls--reminder--close--button',
    rsvpSubmitButtonSelector: '#vw--rsvp-ls--reminder .vw--rsvp-ls--button, #vw--rsvp--reminder .vw--rsvp--button',
    rsvpPrivacyButtonSelector: '#vw--rsvp-ls--reminder label button[title="Privacy policy"], #vw--rsvp--reminder label button[title="Privacy policy"]',
    rsvpAddToCalendarButtonSelector: '#vw--rsvp .vw--rsvp--button, #vw--rsvp-ls .vw--rsvp--button',
    rsvpErrorSelector: '.vw--error, [aria-invalid="true"]',
    progressMilestones: [25, 50, 75, 100],
    startThresholdSeconds: 1,
  };

  const state = {
    channelBound: false,
    boundVideos: new WeakSet(),
    channelMilestones: new WeakMap(),
    rsvpBound: false,
    rsvpOpenVisible: false,
    rsvpSuccessVisible: false,
    rsvpSubmitSnapshot: null,
  };

  const widgetEventMap = {
    videowiseVideoClick: 'video_click',
    videowiseVideoStart: 'video_start',
    videowiseVideoFull: 'video_complete',
    videowiseVideoSwipe: 'video_swipe',
    videowisePlayerClose: 'video_close',
    videowiseVideoSoundOn: 'video_sound_on',
    videowiseVideoSoundOff: 'video_sound_off',
    videowiseProductClick: 'video_product_click',
    videowiseCtaClick: 'video_cta_click',
  };

  const liveEventMap = {
    videowiseLiveOnPlay: 'live_video_play',
    videowiseLiveOnStop: 'live_video_stop',
    videowiseLiveOnInteraction: 'live_video_interaction',
  };

  function log() {
    if (!CONFIG.debug) {
      return;
    }

    console.log('[videowise-tracking]', ...arguments);
  }

  function ensureDataLayer() {
    window.dataLayer = window.dataLayer || [];
    return window.dataLayer;
  }

  function pushEvent(payload) {
    ensureDataLayer().push(payload);
    log('push', payload);
  }

  function cleanText(value) {
    return typeof value === 'string' ? value.trim() : '';
  }

  function pageUrl() {
    return window.location.href;
  }

  function isVisible(element) {
    if (!element) {
      return false;
    }

    const style = window.getComputedStyle(element);
    return style.display !== 'none'
      && style.visibility !== 'hidden'
      && style.opacity !== '0'
      && style.pointerEvents !== 'none';
  }

  function getLiveStreamId() {
    const params = new URLSearchParams(window.location.search);
    return params.get('vwLsId') || '';
  }

  function getLiveTitle() {
    return cleanText(
      document.querySelector('#vw--rsvp--informations--title')?.textContent
      || document.querySelector('.video_title')?.textContent
      || '',
    );
  }

  function rsvpBasePayload(extra) {
    return {
      video_surface: 'videowise_live',
      live_stream_id: getLiveStreamId(),
      live_title: getLiveTitle(),
      page_url: pageUrl(),
      ...extra || {},
    };
  }

  function getRsvpReminder() {
    return document.querySelector(CONFIG.rsvpReminderSelector);
  }

  function getRsvpSuccess() {
    return document.querySelector(CONFIG.rsvpSuccessSelector);
  }

  function getRsvpFieldValue(selector) {
    const field = document.querySelector(selector);
    return cleanText(field?.value || '');
  }

  function getRsvpCheckbox(selector) {
    return Boolean(document.querySelector(selector)?.checked);
  }

  function getRsvpFormSnapshot() {
    return {
      first_name: getRsvpFieldValue('#remind-me-first-name'),
      last_name: getRsvpFieldValue('#remind-me-last-name'),
      email: getRsvpFieldValue('#remind-me-email'),
      phone_prefix: getRsvpFieldValue('#remind-me-prefix'),
      phone_number: getRsvpFieldValue('#remind-me-phone-number'),
      has_phone: getRsvpFieldValue('#remind-me-phone-number') !== '',
      privacy_checked: getRsvpCheckbox('#remind-me-privacy-policy'),
    };
  }

  function getRsvpErrors() {
    const errors = [];
    const firstName = document.querySelector('#remind-me-first-name');
    const lastName = document.querySelector('#remind-me-last-name');
    const email = document.querySelector('#remind-me-email');
    const prefix = document.querySelector('#remind-me-prefix');
    const phone = document.querySelector('#remind-me-phone-number');
    const privacy = document.querySelector('#remind-me-privacy-policy');

    if (firstName?.classList.contains('vw--error')) {
      errors.push('first_name');
    }

    if (lastName?.classList.contains('vw--error')) {
      errors.push('last_name');
    }

    if (email?.classList.contains('vw--error')) {
      errors.push('email');
    }

    if (prefix?.classList.contains('vw--error') || phone?.classList.contains('vw--error')) {
      errors.push('phone');
    }

    if (
      privacy?.classList.contains('vw--error')
      || privacy?.closest('.vw--rsvp--reminder--content--row')?.querySelector('.vw--error')
    ) {
      errors.push('privacy');
    }

    return errors;
  }

  function getChannelHost() {
    return document.querySelector(CONFIG.channelHostSelector);
  }

  function getChannelShadowRoot() {
    return getChannelHost()?.shadowRoot || null;
  }

  function normalizeWidgetDetail(eventName, detail) {
    return {
      event: widgetEventMap[eventName],
      video_surface: 'videowise_widget',
      video_title: detail?.videoTitle || '',
      video_id: detail?.videoId || '',
      video_url: detail?.url || pageUrl(),
      widget_id: detail?.widgetId || '',
      product_id: detail?.productId || '',
      product_name: detail?.productName || '',
      interaction_type: detail?.interactionType || '',
      cta_text: detail?.buttonClicked || '',
      device_type: detail?.deviceType || '',
    };
  }

  function normalizeLiveDetail(eventName, detail) {
    return {
      event: liveEventMap[eventName],
      video_surface: 'videowise_live',
      video_title: detail?.title || '',
      video_id: detail?.id || '',
      video_url: detail?.url || pageUrl(),
      live_stream_id: detail?.lsId || '',
      current_time: Number(detail?.videoElapsedSeconds || 0),
      interaction_type: detail?.interactionType || '',
      interaction_value: detail?.interactionValue || '',
    };
  }

  function channelBasePayload(shadowRoot, videoEl) {
    const title = cleanText(
      shadowRoot?.querySelector('.video_title')?.textContent
      || videoEl?.getAttribute('title')
      || '',
    );
    const duration = Number(videoEl?.duration || 0);
    const currentTime = Number(videoEl?.currentTime || 0);
    const progressPercent = duration > 0
      ? Math.min(100, Math.round((currentTime / duration) * 100))
      : 0;

    return {
      video_surface: 'videowise_channel',
      video_title: title,
      video_url: videoEl?.currentSrc || videoEl?.src || pageUrl(),
      current_time: currentTime,
      duration,
      progress_percent: progressPercent,
    };
  }

  function pushChannelEvent(eventName, shadowRoot, videoEl, extra) {
    pushEvent(
      {
        event: eventName,
        ...channelBasePayload(shadowRoot, videoEl),
        ...extra || {},
      },
    );
  }

  function bindChannelVideo(videoEl, shadowRoot) {
    if (!videoEl || state.boundVideos.has(videoEl)) {
      return;
    }

    state.boundVideos.add(videoEl);
    state.channelMilestones.set(videoEl, {
      started: false,
      hit: {},
    });

    videoEl.addEventListener('play', () => {
      pushChannelEvent('video_play', shadowRoot, videoEl);
    });

    videoEl.addEventListener('pause', () => {
      if (!videoEl.ended) {
        pushChannelEvent('video_pause', shadowRoot, videoEl);
      }
    });

    videoEl.addEventListener('ended', () => {
      pushChannelEvent('video_complete', shadowRoot, videoEl, {
        progress_percent: 100,
      });
    });

    videoEl.addEventListener('timeupdate', () => {
      const milestoneState = state.channelMilestones.get(videoEl);
      const base = channelBasePayload(shadowRoot, videoEl);

      if (!milestoneState.started && base.current_time >= CONFIG.startThresholdSeconds) {
        milestoneState.started = true;
        pushEvent(
          {
            event: 'video_start',
            ...base,
          },
        );
      }

      CONFIG.progressMilestones.forEach((milestone) => {
        if (!milestoneState.hit[milestone] && base.progress_percent >= milestone) {
          milestoneState.hit[milestone] = true;
          pushEvent(
            {
              event: 'video_progress',
              progress_milestone: milestone,
              ...base,
            },
          );
        }
      });
    });
  }

  function bindChannelTracking() {
    const shadowRoot = getChannelShadowRoot();

    if (!shadowRoot || state.channelBound) {
      return Boolean(shadowRoot);
    }

    state.channelBound = true;

    shadowRoot.addEventListener(
      'click',
      (event) => {
        const path = typeof event.composedPath === 'function' ? event.composedPath() : [];
        const card = path.find((node) => node instanceof HTMLElement
            && node.matches(CONFIG.channelCardSelector));

        if (!card) {
          return;
        }

        const title = cleanText(
          card.parentElement?.querySelector(CONFIG.channelCardTitleSelector)?.textContent
          || card.querySelector(CONFIG.channelCardTitleSelector)?.textContent
          || card.querySelector('video')?.getAttribute('title')
          || '',
        );
        const videoUrl = card.querySelector('video')?.currentSrc
          || card.querySelector('video')?.src
          || pageUrl();

        pushEvent({
          event: 'video_click',
          video_surface: 'videowise_channel',
          video_title: title,
          video_url: videoUrl,
        });

        window.setTimeout(() => {
          const playerVideo = shadowRoot.querySelector(CONFIG.channelPlayerVideoSelector);
          bindChannelVideo(playerVideo, shadowRoot);
        }, 250);
      },
      true,
    );

    const playerObserver = new MutationObserver(() => {
      const playerVideo = shadowRoot.querySelector(CONFIG.channelPlayerVideoSelector);
      bindChannelVideo(playerVideo, shadowRoot);
    });

    playerObserver.observe(shadowRoot, {
      childList: true,
      subtree: true,
    });

    bindChannelVideo(shadowRoot.querySelector(CONFIG.channelPlayerVideoSelector), shadowRoot);

    log('channel tracking bound');
    return true;
  }

  function bindWindowEvents(eventMap, normalizer) {
    Object.keys(eventMap).forEach((eventName) => {
      window.addEventListener(eventName, (event) => {
        pushEvent(normalizer(eventName, event.detail || {}));
      });
    });
  }

  function handleRsvpClick(event) {
    const target = event.target instanceof Element ? event.target : null;

    if (!target) {
      return;
    }

    const privacyButton = target.closest(CONFIG.rsvpPrivacyButtonSelector);
    if (privacyButton) {
      pushEvent(
        rsvpBasePayload({
          event: 'live_signup_privacy_policy_click',
        }),
      );
      return;
    }

    const submitButton = target.closest(CONFIG.rsvpSubmitButtonSelector);
    if (submitButton) {
      state.rsvpSubmitSnapshot = getRsvpFormSnapshot();
      pushEvent(
        rsvpBasePayload(
          {
            event: 'live_signup_submit',
            ...state.rsvpSubmitSnapshot,
          },
        ),
      );

      window.setTimeout(() => {
        if (isVisible(getRsvpSuccess())) {
          state.rsvpOpenVisible = false;
          state.rsvpSuccessVisible = true;
          pushEvent(
            rsvpBasePayload(
              {
                event: 'live_signup_submit_success',
                ...state.rsvpSubmitSnapshot || {},
              },
            ),
          );
          return;
        }

        const errors = getRsvpErrors();
        if (errors.length > 0) {
          pushEvent(
            rsvpBasePayload({
              event: 'live_signup_submit_error',
              error_fields: errors.join(','),
            }),
          );
        }
      }, 400);
      return;
    }

    const closeButton = target.closest(CONFIG.rsvpCloseButtonSelector);
    if (closeButton) {
      state.rsvpOpenVisible = false;
      pushEvent(
        rsvpBasePayload({
          event: 'live_signup_modal_close',
          close_method: 'button',
        }),
      );
      return;
    }

    const addToCalendarButton = target.closest(CONFIG.rsvpAddToCalendarButtonSelector);
    if (
      addToCalendarButton
      && cleanText(addToCalendarButton.textContent).toLowerCase().includes('calendar')
    ) {
      pushEvent(
        rsvpBasePayload({
          event: 'live_signup_add_to_calendar',
        }),
      );
    }
  }

  function evaluateRsvpVisibility() {
    const reminderVisible = isVisible(getRsvpReminder());
    const successVisible = isVisible(getRsvpSuccess());

    if (reminderVisible && !state.rsvpOpenVisible) {
      pushEvent(
        rsvpBasePayload({
          event: 'live_signup_modal_open',
        }),
      );
    }

    if (!reminderVisible && state.rsvpOpenVisible) {
      pushEvent(
        rsvpBasePayload({
          event: 'live_signup_modal_close',
          close_method: 'dismiss',
        }),
      );
    }

    if (successVisible && !state.rsvpSuccessVisible) {
      pushEvent(
        rsvpBasePayload(
          {
            event: 'live_signup_submit_success',
            ...state.rsvpSubmitSnapshot || {},
          },
        ),
      );
    }

    state.rsvpOpenVisible = reminderVisible;
    state.rsvpSuccessVisible = successVisible;
  }

  function bindRsvpTracking() {
    if (state.rsvpBound) {
      return;
    }

    state.rsvpBound = true;
    document.addEventListener('click', handleRsvpClick, true);

    const observer = new MutationObserver(() => {
      evaluateRsvpVisibility();
    });

    observer.observe(document.documentElement, {
      attributes: true,
      childList: true,
      subtree: true,
    });

    evaluateRsvpVisibility();
    log('rsvp tracking bound');
  }

  function init() {
    bindWindowEvents(widgetEventMap, normalizeWidgetDetail);
    bindWindowEvents(liveEventMap, normalizeLiveDetail);
    bindRsvpTracking();

    if (bindChannelTracking()) {
      return;
    }

    const rootObserver = new MutationObserver(() => {
      if (bindChannelTracking()) {
        rootObserver.disconnect();
      }
    });

    rootObserver.observe(document.documentElement, {
      childList: true,
      subtree: true,
    });
  }

  init();
})();
